/**
 * LatencyDistribution — Operations tab latency histogram (IA §7.1.3).
 * Milestone v1.4 / Plan 03-05 Task 5.4.
 *
 * Renders a Recharts `BarChart` with 3 health-colored buckets plus a
 * monospace p50 · p95 · p99 summary row underneath.
 *
 * Bucket health palette:
 *   <10s    → success (green)   — healthy response
 *   10-30s  → warning (yellow)  — slow, still acceptable
 *   >30s    → danger (red)      — clearly degraded
 *
 * The five-band histogram (<1s / 1-3s / 3-10s / 10-30s / >30s) was
 * over-partitioned for a DST+Coach pipeline where nearly every trace
 * lands in single-digit seconds; the three-band "fast / slow / stuck"
 * shape is what operators actually need to read at a glance.
 *
 * Colour values are hard-coded hex (same approach Donut.tsx uses) because
 * the theme layer doesn't expose `--success/--warning/--danger` as CSS
 * tokens yet. Keeping the palette local means the chart reads consistently
 * on both themes; if/when the tokens ship, swap to `hsl(var(--*))`.
 *
 * Empty data (every bucket.count === 0 AND total === 0) shows an IA §1.5
 * "no data" state — matches TrendChart / Donut conventions.
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ChartTooltip,
  useChartTooltipTrigger,
  useIsTouchOnly,
} from '@/components/charts/ChartTooltip';
import { chartPalette } from '@/lib/chart-palette';
import { formatLatencyMs } from '@/lib/format';

export interface LatencyBucket {
  /** Bucket label exactly as the backend emits it (<10s, 10-30s, >30s). */
  label: string;
  count: number;
}

export interface LatencyDistributionData {
  buckets: LatencyBucket[];
  p50: number | null;
  p95: number | null;
  p99: number | null;
  total: number;
}

export interface LatencyDistributionProps {
  data: LatencyDistributionData;
  isLoading?: boolean;
  /** Fixed chart height so the page layout doesn't shift on load. */
  height?: number;
  /**
   * Click handler wired to each bucket. When provided, bars become
   * interactive (cursor + hover). The label matches the backend enum
   * (`<10s` / `10-30s` / `>30s`).
   */
  onBarClick?: (band: '<10s' | '10-30s' | '>30s') => void;
}

// Bucket colour by health — 3-band traffic-light semantics. Values come
// from the chart-palette SSOT so the Operations latency histogram speaks
// the same "fast / slow / degraded" vocabulary as every other chart that
// exposes a latency signal (eg. ChartTooltip latency formatter).
// Unknown / unexpected labels fall back to muted grey so a backend
// schema drift degrades gracefully instead of crashing.
const BUCKET_COLOR: Record<string, string> = {
  '<10s': chartPalette.latency.fast,
  '10-30s': chartPalette.latency.slow,
  '>30s': chartPalette.latency.degraded,
};

function bucketFill(label: string): string {
  return BUCKET_COLOR[label] ?? chartPalette.muted;
}

const LATENCY_BAND_LABELS: ReadonlySet<'<10s' | '10-30s' | '>30s'> = new Set([
  '<10s',
  '10-30s',
  '>30s',
]);

function isLatencyBand(
  label: string,
): label is '<10s' | '10-30s' | '>30s' {
  return LATENCY_BAND_LABELS.has(label as '<10s' | '10-30s' | '>30s');
}

export function LatencyDistribution({
  data,
  isLoading = false,
  height = 260,
  onBarClick,
}: LatencyDistributionProps): React.ReactElement {
  // Mobile tap persistence — see ChartTooltip.useChartTooltipTrigger.
  const tooltipTrigger = useChartTooltipTrigger();
  // Touch-only affordance — mirror RatingDistribution: `activeBar` is a
  // hover-only cue, paint a permanent stroke on tap-only devices so the
  // bar still reads as a click target.
  const isTouchOnly = useIsTouchOnly();
  if (isLoading) {
    return (
      <div
        data-testid="latency-distribution-skeleton"
        className="w-full rounded bg-muted animate-pulse"
        style={{ height }}
      />
    );
  }

  // Empty if the backend has no samples at all. A single bucket with count>0
  // still renders as a chart (even if most buckets are zero) — the p50/p95/p99
  // row conveys the shape more than the bars do at small sample sizes.
  const hasAnySample =
    data.total > 0 || data.buckets.some((b) => b.count > 0);

  if (!hasAnySample) {
    return (
      <div
        data-testid="latency-distribution-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No latency samples in range
      </div>
    );
  }

  return (
    <div data-testid="latency-distribution" className="flex flex-col gap-3">
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={data.buckets}
            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis allowDecimals={false} />
            {/*
              bug F1 — themed tooltip matches TimeSavedChart's visual contract.
              v1.4 dashboard-ux-fix (2026-04-24): suppress on touch + drill-down
              so the modal overlay doesn't cover a just-opened tooltip (the
              "tooltip flash" the user reported). Same pattern as
              RatingDistribution — see that file for the longer rationale.
            */}
            {!(isTouchOnly && onBarClick) && (
              <Tooltip
                {...tooltipTrigger}
                cursor={false}
                content={
                  <ChartTooltip
                    valueFormatter={(v) => `Count: ${v ?? 0}`}
                  />
                }
              />
            )}
            <Bar
              dataKey="count"
              isAnimationActive={false}
              cursor={onBarClick ? 'pointer' : 'default'}
              // v1.4 dashboard-ux-fix: foreground-stroke outline on hover so
              // users can see the bar is a click target without reading the
              // cursor. Same affordance as RatingDistribution + Donut's
              // selected-slice stroke, so the drill-down vocabulary reads
              // identically across every interactive chart.
              activeBar={
                onBarClick
                  ? { stroke: 'hsl(var(--foreground))', strokeWidth: 1.5 }
                  : false
              }
              // Touch-only fallback — no hover → no activeBar. Paint the
              // outline permanently but at lower opacity so it still reads
              // as affordance rather than selection.
              stroke={
                onBarClick && isTouchOnly ? 'hsl(var(--foreground))' : undefined
              }
              strokeWidth={onBarClick && isTouchOnly ? 1 : 0}
              strokeOpacity={onBarClick && isTouchOnly ? 0.6 : 1}
              onClick={
                onBarClick
                  ? (payload: unknown) => {
                      const label = (payload as { label?: string })?.label;
                      if (label && isLatencyBand(label)) onBarClick(label);
                    }
                  : undefined
              }
            >
              {data.buckets.map((b) => (
                <Cell key={b.label} fill={bucketFill(b.label)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/*
        Operations P3-1: format.ts and IA §7.1 both reference "P50/P95/P99",
        but the rendered row used lowercase "p50/p95/p99". Uppercase the
        label so the chrome matches the project glossary.
      */}
      <div
        data-testid="latency-distribution-percentiles"
        className="flex flex-wrap items-center gap-x-6 gap-y-1 font-mono text-sm tabular-nums"
      >
        <span data-testid="latency-p50">
          <span className="text-muted-foreground">P50</span>{' '}
          <span className="font-medium">{formatLatencyMs(data.p50)}</span>
        </span>
        <span data-testid="latency-p95">
          <span className="text-muted-foreground">P95</span>{' '}
          <span className="font-medium">{formatLatencyMs(data.p95)}</span>
        </span>
        <span data-testid="latency-p99">
          <span className="text-muted-foreground">P99</span>{' '}
          <span className="font-medium">{formatLatencyMs(data.p99)}</span>
        </span>
      </div>
    </div>
  );
}
